import React, { type FC } from 'react'
import { Box, Flex, Pill, Link } from '@moonpig/launchpad-components'
import { IconSystemLinkExternal } from '@moonpig/launchpad-assets'
import { styled } from '@moonpig/launchpad-utils'
import { useRouter } from '@moonpig/web-core-routing'
import { system as s } from '@moonpig/launchpad-system'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { ProductInfoProduct } from '../../../../types'
import { useLocaleText } from './locale'
import { queryParamsBuilder } from '../../../../utils/queryParamsBuilder'
import { isCardProduct } from '../../../../utils/isCardProduct'
import { transformPill } from '../../../../utils/transformPill'

type Props = {
  product: ProductInfoProduct
  variantKey?: string
}

const StyledSimplifiedBannerContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  ${s({
    paddingLeft: { xs: 5, md: 10 },
    marginTop: 4,
    paddingRight: '57px',
  })}
`

const StyledInfoLink = styled(Link)`
  & {
    display: block;
    text-decoration: none;
    ${s({
      typography: 'typeBodyLabel',
    })}
  }
`

export const SimplifiedModalBanner: FC<Props> = ({ product, variantKey }) => {
  const storeId = useStoreId()
  const router = useRouter()
  const localiseText = useLocaleText()
  const { slug, category, id, masterVariant, variants } = product

  const isCard = product.category && isCardProduct(product.category.department)

  const variant = variants.find(v => v.key === variantKey) ?? masterVariant

  const pill = product.primaryProductPill
    ? transformPill(product.primaryProductPill)
    : undefined

  const groupCardProject =
    router.getCurrentRoute<'search'>()?.params?.groupCardProject

  const queryParams = queryParamsBuilder()
    .add('size', variant.key)
    .add('groupCardProject', groupCardProject)
    .build()

  const href = `/${storeId}/${category.slug}/p/${slug}/${id}/${queryParams}`
  const infoLinkTitle = localiseText('info_link_title')

  return (
    <StyledSimplifiedBannerContainer>
      <Box m={0} p={0}>
        {isCard && !!pill && (
          <Pill label={pill.displayLabel} variant={pill.displayVariant} />
        )}
      </Box>
      <StyledInfoLink
        href={href}
        onClick={() =>
          trackGAEvent(
            createGenericSelectContentEvent({
              eventCategory: 'product details',
              eventAction: 'view more information',
              eventLabel: `${infoLinkTitle}`,
              itemId: id,
            }),
          )
        }
      >
        <Flex alignItems={'center'} justifyContent={'space-between'} gap={8}>
          {infoLinkTitle}
          <IconSystemLinkExternal
            aria-hidden="true"
            style={{ width: 20, height: 20 }}
          />
        </Flex>
      </StyledInfoLink>
    </StyledSimplifiedBannerContainer>
  )
}
